// ─────────────────────────────────────────────────────────────────────────
// Shared date/time formatting helpers — THE ONE place dates are formatted.
// ─────────────────────────────────────────────────────────────────────────
//
// CANONICAL FORMATS (US, 24-hour, NEVER AM/PM — CLAUDE.md §14b.6 / §18.6):
//   • Date only:   MM/DD/YYYY        → fmtLocalDate     (e.g. 06/15/2026)
//   • Date + time: MM/DD/YYYY HH:mm  → fmtLocalDateTime (e.g. 06/15/2026 14:30)
//   • Time only:   HH:mm             → fmtLocalTime
//
// The strings are built from the viewer's LOCAL date components (getMonth/
// getHours/…), NOT a locale formatter, so the output cannot drift to another
// order and a 12-hour / AM-PM token is impossible by construction.  NO date is
// ever formatted inline in a component — everything goes through these helpers.
//
// ── TIMEZONE — two coexistence modes (ADR-0008) ───────────────────────────
// During the XAF↔Alpaca Intelligence migration the wire value is SELF-DESCRIBING
// (LegacyDateTimeJsonConverter):
//   • mode (a) UTC      — AI_* tables → ISO WITH a 'Z'.  Interpreted as UTC.
//   • mode (b) client   — still-Central (XAF-shared) tables → naive ISO, NO 'Z'.
//                         Interpreted in the configured CLIENT timezone.
// Both are then shifted to the VIEWER's local zone for display.  The mode is
// auto-detected from the 'Z' marker, or forced via `opts.sourceMode`.
//
// The client timezone is set ONCE at app start from GET /api/v1/app-config via
// `setClientTimeZone(...)` (see api/appConfig.ts).  It defaults to the same
// value as the backend default so rendering is correct before config loads.
//
// MIGRATION-TEMPORARY: mode (b) — the `client` branch, `clientNaiveToUtcMs`,
// `zonedParts`, the toWire 'client' path, and `setClientTimeZone` — exists only
// while some tables are still Central.  When every table is UTC every value
// carries 'Z', mode collapses to (a), and all of mode (b) is deleted in one
// place with no other change (see CLAUDE.md §14b.6 / ADR-0008).
// ─────────────────────────────────────────────────────────────────────────

/** Source timezone of a STORED value: 'utc' = AI/UTC table (mode a),
 *  'client' = still-Central table (mode b, MIGRATION-TEMPORARY). */
export type DateSourceMode = 'utc' | 'client';

export interface FmtOpts {
  /** Force the source mode instead of auto-detecting from the 'Z' marker. */
  sourceMode?: DateSourceMode;
}

// ── Client timezone (MIGRATION-TEMPORARY — only used by mode (b)) ───────────
// Default matches the backend default (DateTime:ClientTimeZone) so mode-(b)
// values render correctly even before /app-config has loaded.
let _clientTimeZone = 'America/Chicago';

/** Set the client (lab) timezone — called once after GET /api/v1/app-config.
 *  MIGRATION-TEMPORARY (ADR-0008): only affects naive (mode b) values. */
export function setClientTimeZone(iana: string | null | undefined): void {
  if (iana && iana.trim()) _clientTimeZone = iana.trim();
}

/** The currently configured client timezone (IANA id). */
export function getClientTimeZone(): string {
  return _clientTimeZone;
}

// ── Per-form date-source override (MIGRATION-TEMPORARY, ADR-0008) ───────────
// 'auto' = decide per value from the 'Z' marker (default). 'utc' / 'client' =
// force every date on the ACTIVE form to that source zone. AppShell sets this
// from the App Config per-form map as each form opens, BEFORE its date columns
// render (a module setter, not React state). An explicit fmtLocal* `sourceMode`
// always wins over this.
let _activeFormMode: 'auto' | DateSourceMode = 'auto';

/** Set the active form's date-source override. Call before the form renders. */
export function setActiveFormDateMode(mode: 'auto' | DateSourceMode): void {
  _activeFormMode = mode === 'utc' || mode === 'client' ? mode : 'auto';
}

/** True when the ISO string already carries a timezone marker ('Z' or ±hh:mm). */
function hasZoneMarker(iso: string): boolean {
  return /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
}

/** Append 'Z' to a timestamp string if it has no timezone marker. */
export function forceUtc(iso: string): string {
  return hasZoneMarker(iso) ? iso : iso + 'Z';
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// ── Intl zone helpers (no external dependency) ─────────────────────────────
/** Milliseconds the IANA `zone` is ahead of UTC at the given UTC instant. */
function zoneOffsetMs(utcMs: number, zone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = dtf.formatToParts(new Date(utcMs));
  const get = (type: string): number => Number(p.find((x) => x.type === type)?.value ?? 0);
  const asZoned = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asZoned - utcMs;
}

/** MIGRATION-TEMPORARY (ADR-0008): interpret a naive "YYYY-MM-DDTHH:mm:ss"
 *  wall-clock as being in `zone`, return the matching UTC instant (ms). */
function clientNaiveToUtcMs(naive: string, zone: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(naive);
  if (!m) return NaN;
  const [, y, mo, d, h, mi, s] = m;
  const guessUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), s ? Number(s) : 0);
  // Correct the guess by the zone's offset at that instant.
  return guessUtc - zoneOffsetMs(guessUtc, zone);
}

/** Wall-clock parts of a UTC instant rendered in `zone`. */
function zonedParts(date: Date, zone: string): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = dtf.formatToParts(date);
  const get = (type: string): number => Number(p.find((x) => x.type === type)?.value ?? 0);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') };
}

// ── Timezone conversion KILL-SWITCH (TEMPORARY, manager decision 2026-06-15) ──
// During the migration/coexistence period ALL UTC↔local conversion is DISABLED:
// dates are saved as the SERVER's local time and DISPLAYED exactly as stored,
// with NO timezone shift.  The canonical FORMAT (MM/DD/YYYY [HH:mm], 24-hour, no
// AM/PM) is unchanged — only the conversion is switched off.  This is the ONE
// place it is disabled (both display via parseToDate and save via toWire); the
// original conversion code is kept intact below the flag so UTC can be re-enabled
// by flipping this single flag to false once every form is migrated.
// (CLAUDE.md §14b.6 / ADR-0008.)  Typed `boolean` (not the literal `true`) so the
// retained conversion branches stay reachable/referenced.
const TZ_CONVERSION_DISABLED: boolean = true;

/** Parse a wire value to a LOCAL Date from its LITERAL calendar parts, ignoring
 *  any 'Z'/offset — used while conversion is disabled so getMonth()/getHours()/…
 *  return exactly the stored wall-clock with no shift. */
function parseLiteral(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);
  if (m) {
    const [, y, mo, d, h, mi, s] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d),
      h ? Number(h) : 0, mi ? Number(mi) : 0, s ? Number(s) : 0);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Parse a wire value to a Date.  While TZ_CONVERSION_DISABLED, the value is read
 * as its literal wall-clock (no shift).  Otherwise (UTC re-enabled): choose the
 * SOURCE zone by `mode` (explicit) or the value's 'Z' marker (auto), so calling
 * getHours()/getMonth()/… yields the VIEWER's browser-local time.
 */
function parseToDate(value: string | null | undefined, mode?: DateSourceMode): Date | null {
  if (!value) return null;
  if (TZ_CONVERSION_DISABLED) return parseLiteral(value);
  // ── Original UTC↔local conversion (re-enabled by flipping the flag) ──
  // Priority: explicit opts.sourceMode > active-form override > auto-detect ('Z').
  const effective: DateSourceMode =
    mode
    ?? (_activeFormMode !== 'auto' ? _activeFormMode : (hasZoneMarker(value) ? 'utc' : 'client'));
  const d = effective === 'utc'
    ? new Date(forceUtc(value))
    : new Date(clientNaiveToUtcMs(value, _clientTimeZone)); // mode (b) MIGRATION-TEMPORARY
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "06/15/2026" — local date only, MM/DD/YYYY. */
export function fmtLocalDate(value: string | null | undefined, opts?: FmtOpts): string {
  const d = parseToDate(value, opts?.sourceMode);
  if (!d) return '';
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
}

/** "06/15/2026 14:30" — local date + time, MM/DD/YYYY HH:mm, 24-hour, no AM/PM.
 *  THE canonical date+time format (grid cells AND date-column filters). */
export function fmtLocalDateTime(value: string | null | undefined, opts?: FmtOpts): string {
  const d = parseToDate(value, opts?.sourceMode);
  if (!d) return '';
  return (
    `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}` +
    ` ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  );
}

/** Timezone abbreviation ("CDT"/"CST") for an instant in a given IANA zone.
 *  Intl lives here and only here — utils/datetime.ts is the single file allowed
 *  to touch locale/zone APIs (guardrail C2). */
function zoneAbbrev(d: Date, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(d);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * "2026-07-28 14:45 CDT" — the timestamp inside an ELECTRONIC SIGNATURE
 * manifestation, and nowhere else.
 *
 * <p>Deliberately NOT the app-wide `MM/DD/YYYY HH:mm` (§18.6 / standing rule 3).
 * A signature manifestation is a legal artefact rather than a grid cell: an
 * unambiguous, sortable ISO date plus an explicit zone means the signing moment
 * cannot be misread as 07/28 vs 28/07 by a reader in another locale, or left
 * ambiguous across a DST change. Every OTHER date on the form keeps the
 * canonical format — do not reuse this helper for grid columns.</p>
 *
 * <p>The zone shown is the CLIENT (lab) timezone, not the viewer's: the stamp
 * records where the signature was made. While TZ_CONVERSION_DISABLED the stored
 * wall-clock is already lab-local, so the two agree. The abbreviation is derived
 * from the parsed instant, so it can be off only for a signature made within a
 * few hours of a DST transition viewed from a very different zone.</p>
 */
export function fmtSignatureStamp(value: string | null | undefined, opts?: FmtOpts): string {
  const d = parseToDate(value, opts?.sourceMode);
  if (!d) return '';
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const zone = zoneAbbrev(d, _clientTimeZone);
  return zone ? `${date} ${time} ${zone}` : `${date} ${time}`;
}

/** "14:30" — local time only, 24-hour, no AM/PM.  Accepts a Date or ISO string. */
export function fmtLocalTime(
  value: Date | string | null | undefined = new Date(),
  opts?: FmtOpts,
): string {
  const dt = typeof value === 'string' ? parseToDate(value, opts?.sourceMode) : (value ?? new Date());
  if (!dt || Number.isNaN(dt.getTime())) return '';
  return `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

/**
 * `fmtLocal` and `fmtLocalShort` are kept as aliases of the canonical date+time
 * format so the dozens of existing call sites keep working but all render the
 * single MM/DD/YYYY HH:mm form (no seconds, no comma, no AM/PM).  Use
 * `fmtLocalDate` when a value is a pure date.
 */
export const fmtLocal = fmtLocalDateTime;
export const fmtLocalShort = fmtLocalDateTime;

/** "06/15/2026 14:30" — a UTC instant rendered in an ARBITRARY IANA `zone`
 *  (MM/DD/YYYY HH:mm, 24-hour, no AM/PM).  Used by the Date & Time settings
 *  preview; normal display code uses the fmtLocal* helpers (viewer-local). */
export function fmtInZone(date: Date, zone: string): string {
  if (Number.isNaN(date.getTime())) return '';
  const p = zonedParts(date, zone);
  return `${pad2(p.mo)}/${pad2(p.d)}/${p.y} ${pad2(p.h)}:${pad2(p.mi)}`;
}

/**
 * Convert a viewer-local Date the user picked into the wire string for SAVING,
 * in the column's SOURCE zone:
 *   • 'utc'    → UTC ISO with 'Z'        (AI/UTC tables — save UTC).
 *   • 'client' → naive client-local ISO  (still-Central tables — MIGRATION-
 *                TEMPORARY ADR-0008 / standing-rule #2: write client-local
 *                wall-clock, NEVER raw UTC into a column XAF still reads).
 * The backend converter round-trips this symmetrically (Read keeps the mode).
 */
export function toWire(local: Date, mode: DateSourceMode = 'client'): string {
  if (Number.isNaN(local.getTime())) return '';
  // TEMPORARY (manager decision 2026-06-15): conversion disabled — save the
  // LITERAL local wall-clock the user picked (no UTC/zone shift).  Re-enable UTC
  // by flipping TZ_CONVERSION_DISABLED to false (restores the branch below).
  if (TZ_CONVERSION_DISABLED) {
    return `${local.getFullYear()}-${pad2(local.getMonth() + 1)}-${pad2(local.getDate())}` +
      `T${pad2(local.getHours())}:${pad2(local.getMinutes())}:${pad2(local.getSeconds())}`;
  }
  if (mode === 'utc') return local.toISOString();
  const p = zonedParts(local, _clientTimeZone); // mode (b) MIGRATION-TEMPORARY
  return `${p.y}-${pad2(p.mo)}-${pad2(p.d)}T${pad2(p.h)}:${pad2(p.mi)}:${pad2(p.s)}`;
}

/** "Now", as a naive wire string, using THIS machine's/browser's own raw
 *  wall-clock (not converted to the lab's configured timezone). For "auto-fill
 *  this field with what the server is about to stamp" preview scenarios (e.g.
 *  ticking a row to preview ValidatedDate/ApprovedDate before Submit).
 *
 *  CHANGED 2026-07-20 (explicit, twice-confirmed user request): previously
 *  this converted into the LAB's timezone (`_clientTimeZone`) specifically to
 *  avoid an out-of-timezone reviewer seeing a preview offset from the lab's
 *  wall clock (confirmed 2026-07-17: an IST browser previewed an ApprovedDate
 *  ~10.5h off). The user explicitly asked to revert that for THIS preview —
 *  they want it to reflect their own PC's current time, not the lab's. Note
 *  this is now purely cosmetic either way: doAction (ResultValidationDetail)
 *  no longer sends an UNTOUCHED preview row's stamp to the server at all
 *  (2026-07-20 fix) — the value actually persisted always comes from the SQL
 *  Server's own GETDATE() at Validate/Approve time, regardless of what this
 *  preview shows. A genuinely manually-edited stamp (via the inline date
 *  picker) still validates as "not in the future" against THIS same raw local
 *  clock (see onCellCommit's dateCurrentMsg check), so the two stay
 *  consistent with each other. */
export function nowClientWire(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

// ── Lab-timezone EXPORT helpers (report exports) ────────────────────────────
// Report EXPORTS must show the LAB's wall-clock so a Houston collect time reads
// the same whether the file is opened in Houston or Chennai — they format in a
// fixed zone, NOT the viewer's. Override per-deployment via VITE_LAB_TIMEZONE.
// NOTE (ADR-0008): while TZ_CONVERSION_DISABLED is on the wire value is already
// the stored wall-clock (no shift), so "lab time" == the literal stored value —
// these delegate to the no-shift local format. When UTC is re-enabled they
// convert the UTC instant into LAB_TZ (the same single flag flips both).
const LAB_TZ = (import.meta.env.VITE_LAB_TIMEZONE as string | undefined) || 'America/Chicago';

/** "06/10/2026 10:24" — MM/DD/YYYY HH:mm in the LAB's timezone (export-safe). */
export function fmtLabDateTime(value: string | null | undefined): string {
  if (!value) return '';
  if (TZ_CONVERSION_DISABLED) return fmtLocalDateTime(value);
  const d = new Date(forceUtc(value));
  if (Number.isNaN(d.getTime())) return '';
  const p = zonedParts(d, LAB_TZ);
  return `${pad2(p.mo)}/${pad2(p.d)}/${p.y} ${pad2(p.h)}:${pad2(p.mi)}`;
}

/** "06/10/2026" — MM/DD/YYYY date-only in the LAB's timezone (export-safe). */
export function fmtLabDate(value: string | null | undefined): string {
  if (!value) return '';
  if (TZ_CONVERSION_DISABLED) return fmtLocalDate(value);
  const d = new Date(forceUtc(value));
  if (Number.isNaN(d.getTime())) return '';
  const p = zonedParts(d, LAB_TZ);
  return `${pad2(p.mo)}/${pad2(p.d)}/${p.y}`;
}

/** Format one cell value for a CSV/Excel export: ISO datetime → MM/DD/YYYY
 *  HH:mm, ISO date-only → MM/DD/YYYY (matching the grid); any non-date value
 *  passes through as a plain string. */
export function csvCell(v: unknown): string {
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return fmtLocalDateTime(v) || v;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtLocalDate(v) || v;
  }
  return v == null ? '' : String(v);
}

// SQL Server's default datetime text (style 0, e.g. "Apr 1 2026 1:05PM") — what
// a CAST(datetime AS nvarchar) produces in the module distinct query.
const SQL_DT = /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;
const MONTHS3: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * Formats a column-filter option value to the app-wide MM/DD/YYYY HH:mm.
 * Filter lists arrive as ISO strings OR SQL's default datetime text
 * ("Apr 1 2026 1:05PM"); both render to the same format the grid cell shows.
 * Anything not a recognised date passes through unchanged (non-date columns are
 * untouched). DISPLAY only — the caller keeps the raw value for matching.
 */
export function fmtFilterOption(v: string | null | undefined): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(v)) {
    const f = fmtLocalDateTime(v);
    if (f) return f;
  }
  const m = SQL_DT.exec(v.trim());
  if (m) {
    const mon = MONTHS3[m[1]!.toLowerCase()];
    if (mon) {
      const day = Number(m[2]); const year = Number(m[3]);
      let hour = Number(m[4]); const min = Number(m[5]);
      const ap = m[7]!.toUpperCase();
      if (ap === 'PM' && hour < 12) hour += 12;
      if (ap === 'AM' && hour === 12) hour = 0;
      return `${pad2(mon)}/${pad2(day)}/${year} ${pad2(hour)}:${pad2(min)}`;
    }
  }
  return v;
}
